import {useMemo} from 'react';

import {
  SearchCuisineTaxonomy,
  useRecommendedDefaultAddressQuery,
  useSearchQuery,
} from '@/graphql/types';
import {
  BREAKFAST_QUERY_LIMIT,
  buildCards,
  filterNonNullNodes,
  POPULAR_QUERY_LIMIT,
  TILES_PER_CARD,
} from './collectionOfFilters.helpers';

const useCollectionOfFilters = () => {
  const {data: addressData, loading: addressLoading} = useRecommendedDefaultAddressQuery({
    variables: {useGeoIP: true},
  });
  const recommendedDefaultAddress = addressData?.identity?.recommendedDefaultAddress;

  const searchAddress = useMemo(() => {
    if (recommendedDefaultAddress) {
      return {
        city: recommendedDefaultAddress.city || '',
        state: recommendedDefaultAddress.state || '',
        latitude: recommendedDefaultAddress.latitude ?? null,
        longitude: recommendedDefaultAddress.longitude ?? null,
        street: recommendedDefaultAddress.street || '',
      };
    }
    return null;
  }, [recommendedDefaultAddress]);

  const {data: popularData, loading: popularLoading} = useSearchQuery({
    variables: {
      address: searchAddress || {city: '', state: '', latitude: null, longitude: null, street: ''},
      limit: POPULAR_QUERY_LIMIT,
      offset: 0,
      filter: {},
    },
    skip: !searchAddress,
    fetchPolicy: 'no-cache',
  });

  const {data: breakfastData, loading: breakfastLoading} = useSearchQuery({
    variables: {
      address: searchAddress || {city: '', state: '', latitude: null, longitude: null, street: ''},
      limit: BREAKFAST_QUERY_LIMIT,
      offset: 0,
      filter: {
        taxonomiesAndTaxons: {cuisineTaxonomy: SearchCuisineTaxonomy.Breakfast},
      },
    },
    skip: !searchAddress,
    fetchPolicy: 'no-cache',
  });

  const loading = addressLoading || popularLoading || breakfastLoading;

  const {cards} = useMemo(
    () =>
      buildCards(
        filterNonNullNodes(popularData?.marketplaceSearchV2?.caterers?.nodes),
        filterNonNullNodes(breakfastData?.marketplaceSearchV2?.caterers?.nodes),
      ),
    [popularData, breakfastData],
  );

  const allCardsAreFull = cards.every(card => card.items.length >= TILES_PER_CARD);

  const shouldShowCollectionOfFilters = loading || allCardsAreFull;

  return {
    shouldShowCollectionOfFilters,
    cards,
    searchAddress,
    loading,
  };
};

export default useCollectionOfFilters;
